// Helpers de pedidos compartilhados entre Maps/index.js (planejamento) e
// Maps/Areas/index.js (cadastro de áreas — usa só visualização, read-only).

export const EXCLUDED_STATUS = new Set(['aberto', 'devolvido', 'em devolução', 'cancelado'])
// v10: bump pra invalidar entradas ruins (validação por UF aceita coords erradas
// dentro do mesmo estado — ex: AwesomeAPI devolveu (-23.08,-49.54) para CEP em
// Osasco/SP, que está dentro de SP mas a 280km de Osasco). v10 adiciona checagem
// por raio (~50km) contra o centro da cidade conhecida.
export const GEO_LS_KEY = 'santolog_geocache_v10'

// ─── Bounding boxes — Brasil e por UF ───────────────────────────────────────
// [latMin, latMax, lngMin, lngMax] com margem de ~0.1° nas bordas.
// Usados pra validar lat/lng vindos das APIs de CEP — se um API retornar coords
// fora do UF que ele mesmo declarou, descartamos esse candidato.
const BR_BOUNDS = [-34.1, 5.5, -74.1, -33.7]
export const UF_BOUNDS = {
  AC: [-11.4, -7.0, -74.1, -66.5],
  AL: [-10.6, -8.7, -38.4, -34.9],
  AM: [-9.9, 2.3, -74.0, -56.0],
  AP: [-1.4, 4.6, -55.0, -49.8],
  BA: [-18.5, -8.4, -46.7, -37.2],
  CE: [-8.0, -2.6, -41.6, -37.1],
  DF: [-16.2, -15.4, -48.4, -47.2],
  ES: [-21.4, -17.8, -42.0, -39.6],
  GO: [-19.6, -12.3, -53.4, -45.8],
  MA: [-10.4, -0.9, -48.9, -41.6],
  MG: [-23.0, -14.1, -51.2, -39.7],
  MS: [-24.2, -17.1, -58.3, -50.8],
  MT: [-18.1, -7.2, -61.8, -50.1],
  PA: [-10.0, 2.7, -59.0, -45.9],
  PB: [-8.4, -5.9, -38.9, -34.6],
  PE: [-9.6, -7.2, -41.5, -34.7],
  PI: [-11.0, -2.6, -46.0, -40.3],
  PR: [-26.8, -22.4, -54.7, -47.9],
  RJ: [-23.5, -20.6, -45.0, -40.8],
  RN: [-6.7, -4.7, -38.7, -34.8],
  RO: [-13.8, -7.8, -66.9, -59.7],
  RR: [-1.7, 5.4, -64.9, -58.8],
  RS: [-33.9, -26.9, -57.8, -49.6],
  SC: [-29.5, -25.8, -54.0, -48.2],
  SE: [-11.7, -9.3, -38.1, -36.3],
  SP: [-25.5, -19.7, -53.3, -44.1],
  TO: [-13.6, -5.1, -50.9, -45.6],
}

export function isInBrazil(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  return lat >= BR_BOUNDS[0] && lat <= BR_BOUNDS[1]
    && lng >= BR_BOUNDS[2] && lng <= BR_BOUNDS[3]
}

export function isInUF(lat, lng, uf) {
  const b = UF_BOUNDS[String(uf || '').toUpperCase()]
  if (!b) return isInBrazil(lat, lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  return lat >= b[0] && lat <= b[1] && lng >= b[2] && lng <= b[3]
}

// Distância em km entre dois pontos [lat,lng] (fórmula de Haversine)
export function haversineKm(a, b) {
  if (!a || !b) return Infinity
  const R = 6371, toRad = d => d * Math.PI / 180
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1])
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

// Coordenadas aproximadas de cidades (fallback quando não houver lat/lng do CEP em cache)
export const CITY_COORDS = {
  "são paulo|sp": [-23.5505, -46.6333], "rio de janeiro|rj": [-22.9068, -43.1729],
  "brasília|df": [-15.7801, -47.9292], "brasilia|df": [-15.7801, -47.9292],
  "salvador|ba": [-12.9714, -38.5014], "fortaleza|ce": [-3.7172, -38.5434],
  "belo horizonte|mg": [-19.9167, -43.9345], "manaus|am": [-3.1190, -60.0217],
  "curitiba|pr": [-25.4290, -49.2671], "recife|pe": [-8.0578, -34.8829],
  "porto alegre|rs": [-30.0346, -51.2177], "belém|pa": [-1.4554, -48.4898],
  "goiânia|go": [-16.6869, -49.2648], "florianópolis|sc": [-27.5954, -48.5480],
  "são luís|ma": [-2.5297, -44.3028], "maceió|al": [-9.6658, -35.7350],
  "natal|rn": [-5.7945, -35.2120], "teresina|pi": [-5.0919, -42.8034],
  "campo grande|ms": [-20.4428, -54.6460], "joão pessoa|pb": [-7.1195, -34.8450],
  "aracaju|se": [-10.9095, -37.0748], "porto velho|ro": [-8.7619, -63.9039],
  "cuiabá|mt": [-15.6014, -56.0979], "macapá|ap": [0.0356, -51.0705],
  "rio branco|ac": [-9.9754, -67.8249], "palmas|to": [-10.2491, -48.3243],
  "boa vista|rr": [2.8235, -60.6758], "vitória|es": [-20.3222, -40.3381],
  "guarulhos|sp": [-23.4543, -46.5335], "campinas|sp": [-22.9056, -47.0608],
  "são bernardo do campo|sp": [-23.6914, -46.5646], "santo andré|sp": [-23.6737, -46.5382],
  "osasco|sp": [-23.5324, -46.7920], "ribeirão preto|sp": [-21.1775, -47.8208],
  "sorocaba|sp": [-23.5015, -47.4526], "mauá|sp": [-23.6678, -46.4614],
  "diadema|sp": [-23.6861, -46.6228], "jundiaí|sp": [-23.1864, -46.8964],
  "carapicuíba|sp": [-23.5225, -46.8354], "mogi das cruzes|sp": [-23.5228, -46.1886],
  "santos|sp": [-23.9619, -46.3322], "piracicaba|sp": [-22.7253, -47.6492],
  "são josé dos campos|sp": [-23.2237, -45.9009], "bauru|sp": [-22.3246, -49.0703],
  "contagem|mg": [-19.9317, -44.0536], "juiz de fora|mg": [-21.7642, -43.3503],
  "são josé do rio preto|sp": [-20.8197, -49.3794], "londrina|pr": [-23.3045, -51.1696],
  "maringá|pr": [-23.4273, -51.9375], "joinville|sc": [-26.3045, -48.8487],
  "blumenau|sc": [-26.9195, -49.0661], "feira de santana|ba": [-12.2664, -38.9663],
  "anápolis|go": [-16.3281, -48.9530], "uberlândia|mg": [-18.9133, -48.2775],
  "caxias do sul|rs": [-29.1678, -51.1795], "pelotas|rs": [-31.7654, -52.3375],
  "nova iguaçu|rj": [-22.7556, -43.4558], "duque de caxias|rj": [-22.7861, -43.3116],
  "niterói|rj": [-22.8832, -43.1034], "cajamar|sp": [-23.3581, -46.8772],
  "nova lima|mg": [-19.9849, -43.8471], "varginha|mg": [-21.5513, -45.4305],
  "ponta grossa|pr": [-25.0916, -50.1619], "cascavel|pr": [-24.9555, -53.4552],
  "chapecó|sc": [-27.1005, -52.6156], "criciúma|sc": [-28.6780, -49.3697],
  "itajaí|sc": [-26.9078, -48.6614], "vitória da conquista|ba": [-14.8661, -40.8444],
  "barueri|sp": [-23.5047, -46.8763], "cotia|sp": [-23.6029, -46.9196],
  "taubaté|sp": [-23.0268, -45.5556], "franca|sp": [-20.5386, -47.4006],
  "limeira|sp": [-22.5643, -47.4014], "suzano|sp": [-23.5426, -46.3114],
  "sumaré|sp": [-22.8219, -47.2672], "indaiatuba|sp": [-23.0904, -47.2186],
  "praia grande|sp": [-24.0059, -46.4019],
  // RM SP — leste / nordeste
  "itaquaquecetuba|sp": [-23.4865, -46.3486], "poá|sp": [-23.5278, -46.3437],
  "ferraz de vasconcelos|sp": [-23.5414, -46.3692], "arujá|sp": [-23.3963, -46.3214],
  "itapevi|sp": [-23.5491, -46.9342], "embu das artes|sp": [-23.6493, -46.8527],
  "embu|sp": [-23.6493, -46.8527], "taboão da serra|sp": [-23.6266, -46.7919],
  "santana de parnaíba|sp": [-23.4441, -46.9181], "caieiras|sp": [-23.3622, -46.7404],
  "francisco morato|sp": [-23.2820, -46.7444], "franco da rocha|sp": [-23.3231, -46.7269],
  "mairiporã|sp": [-23.3192, -46.5867],
}

export function loadGeoCache() {
  try {
    for (let i = 2; i <= 9; i++) localStorage.removeItem('santolog_geocache_v' + i)
    return JSON.parse(localStorage.getItem(GEO_LS_KEY) || '{}')
  } catch { return {} }
}
export function saveGeoCache(cache) {
  try { localStorage.setItem(GEO_LS_KEY, JSON.stringify(cache)) } catch { }
}

export function normCep(v) { return String(v || '').replace(/\D/g, '').padStart(8, '0') }
export function normCityKey(c, u) {
  return `${String(c || '').toLowerCase().trim()}|${String(u || '').toLowerCase().trim()}`
}
export function parsePeso(v) {
  if (v === null || v === undefined || v === '') return 0
  const n = parseFloat(String(v).replace(',', '.'))
  return isNaN(n) ? 0 : n
}
export function fmtKg(n) {
  return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

// Mapper: resposta da API → formato interno usado pelos dois mapas
export function mapOrderFromApi(item) {
  const g = (...keys) => { for (const k of keys) { if (item[k] != null && item[k] !== '') return item[k] } return '' }
  return {
    'STATUS PED.': g('StatusOrder', 'statusOrder', 'status', 'STATUS PED.', 'Status'),
    'CIDADE': g('City', 'city', 'cidade', 'CIDADE', 'Cidade', 'ShippingCity'),
    'UF': g('State', 'state', 'uf', 'UF', 'ShippingState'),
    'CEP': g('ZipCode', 'zipCode', 'cep', 'CEP', 'PostalCode', 'ShippingPostalCode'),
    'PEDIDO': g('Order', 'order', 'pedido', 'PEDIDO', 'IDOrder', 'NrOrder', 'NrPedido'),
    'TRANSPORTADORA': g('Carrier', 'carrier', 'transportadora', 'TRANSPORTADORA', 'CarrierName', 'SupplierNameCorporateName'),
    'PESO (KG)': g('Weight', 'weight', 'peso', 'PESO (KG)', 'WeightKg'),
    'DT CRIAÇÃO': g('CreatedAt', 'createdAt', 'dtCriacao', 'DT CRIAÇÃO', 'DtCriacao', 'Recordtimestamp'),
    'DT ENTREGA EST.': g('EstimatedDelivery', 'estimatedDelivery', 'DT ENTREGA EST.', 'DtEntregaEst', 'ShippingEstimateDate'),
  }
}

// Resolve coordenadas para uma linha mapeada usando cache de CEP + fallback de cidade.
// Retorna [lat, lng, src] ou null se não encontrar.
// Defesa em profundidade:
//  (1) se a coord cacheada não bate com o UF do pedido → descarta;
//  (2) se a cidade do pedido existe no CITY_COORDS e a coord cacheada está a >50km
//      do centro dessa cidade → descarta também (caso o cache tenha sido populado
//      antes de validações por raio existirem).
const MAX_KM_CACHE_DIST = 50
export function resolveCoords(row, cache = {}) {
  const cep = normCep(row['CEP'])
  const uf = String(row['UF'] || '').toUpperCase()
  const ck = normCityKey(row['CIDADE'], row['UF'])
  const cityCoords = CITY_COORDS[ck]
  const fromCep = cache[cep]

  if (Array.isArray(fromCep) && fromCep.length === 2
    && Number.isFinite(fromCep[0]) && Number.isFinite(fromCep[1])) {
    const ufOk = !uf || isInUF(fromCep[0], fromCep[1], uf)
    const cityOk = !cityCoords || haversineKm(fromCep, cityCoords) <= MAX_KM_CACHE_DIST
    if (ufOk && cityOk) {
      return [fromCep[0], fromCep[1], 'cep']
    }
    // cacheada inconsistente — cai no fallback de cidade
  }
  if (cityCoords) return [cityCoords[0], cityCoords[1], 'cidade']
  return null
}

let _uidCounter = 0
function uid() {
  _uidCounter = (_uidCounter + 1) >>> 0
  return Date.now().toString(36) + _uidCounter.toString(36)
}

// Pega rows mapeados (mapOrderFromApi), aplica filtro de status/CEP e resolve coords
// usando o cache localStorage. Aplica jitter para separar markers no mesmo CEP.
// Não faz chamadas externas — pedidos sem cache caem no fallback de cidade ou são descartados.
export function geocodeOrdersFromCache(rows, cache) {
  return rows.map(r => {
    const coords = resolveCoords(r, cache)
    if (!coords) return null
    const jitter = coords[2] === 'cidade' ? 0.008 : 0.0001
    return {
      ...r,
      _id: uid(),
      _cep: normCep(r['CEP']),
      _key: normCityKey(r['CIDADE'], r['UF']),
      _lat: coords[0] + (Math.random() - 0.5) * jitter,
      _lng: coords[1] + (Math.random() - 0.5) * jitter,
      _peso: parsePeso(r['PESO (KG)']),
      _geoSrc: coords[2],
    }
  }).filter(Boolean)
}

// Filtra orders pelo status excluído e CEPs inválidos
export function filterValidOrders(rows) {
  return rows.filter(r => {
    const st = (r['STATUS PED.'] || '').toLowerCase().trim()
    return !EXCLUDED_STATUS.has(st) && normCep(r['CEP']).length >= 5
  })
}
